import { useMemo, useRef, useState, useEffect } from "react";
import { Download, FileText, FileSpreadsheet, ChevronDown } from "lucide-react";
import { API_URL } from "../lib/api";

const MONTHS = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

function currentMonthValue() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function monthOptions() {
  const now = new Date();
  const list = [];
  for (let i = 0; i < 12; i += 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    list.push({ value, label: `${MONTHS[d.getMonth()]} ${d.getFullYear()}` });
  }
  return list;
}

async function downloadFile(url, filename, setError) {
  try {
    const token = window.localStorage.getItem("session_token");
    const response = await fetch(url, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!response.ok) {
      const text = await response.text().catch(() => "");
      throw new Error(text || `HTTP ${response.status}`);
    }
    const blob = await response.blob();
    const link = document.createElement("a");
    const href = URL.createObjectURL(blob);
    link.href = href;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(href), 1000);
  } catch (err) {
    setError?.(err.message || "No pudimos generar el archivo.");
  }
}

export default function ExportMenu({ resource, label = "Exportar", testid = "export-menu", snapshot = false }) {
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(currentMonthValue());
  const [error, setError] = useState("");
  const ref = useRef(null);
  const months = useMemo(monthOptions, []);

  useEffect(() => {
    const onClick = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const trigger = (format) => {
    const url = snapshot
      ? `${API_URL}/exports/${resource}/${format}`
      : `${API_URL}/exports/${resource}/${format}?month=${month}`;
    const nameMap = { dashboard: "resumen", incomes: "ingresos", expenses: "egresos", cash: "caja", inventory: "inventario", cancellations: "cancelaciones" };
    const today = new Date().toISOString().slice(0, 10);
    const suffix = snapshot ? today : month;
    const filename = `${nameMap[resource] || resource}-${suffix}.${format}`;
    downloadFile(url, filename, setError);
    setOpen(false);
  };

  return (
    <div className="export-menu" ref={ref} data-testid={testid}>
      <button
        type="button"
        className="expenses-scan-button"
        data-testid={`${testid}-trigger`}
        onClick={() => setOpen((v) => !v)}
      >
        <Download size={17} />
        {label}
        <ChevronDown size={15} />
      </button>
      {open && (
        <div className="export-menu-pop" data-testid={`${testid}-popover`}>
          {!snapshot && (
            <label className="export-menu-month">
              <small>Mes</small>
              <select value={month} onChange={(e) => setMonth(e.target.value)} data-testid={`${testid}-month`}>
                {months.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </label>
          )}
          <button type="button" onClick={() => trigger("csv")} data-testid={`${testid}-csv`}>
            <FileSpreadsheet size={16} /> Descargar CSV
          </button>
          <button type="button" onClick={() => trigger("pdf")} data-testid={`${testid}-pdf`}>
            <FileText size={16} /> Descargar PDF
          </button>
          {error && <p className="export-menu-error" data-testid={`${testid}-error`}>{error}</p>}
        </div>
      )}
    </div>
  );
}
